import React, { useEffect, useRef, useState, useCallback } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Mic, Square, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * DictationTextarea
 * Textarea con dettatura vocale integrata (Web Speech API, lingua it-IT).
 * - Gratuito, nessuna chiave API, trascrizione in tempo reale lato browser.
 * - Supporto ottimale su Chrome/Edge (anche Android). Safari parziale, Firefox non supportato.
 *
 * Comandi vocali di punteggiatura/formattazione riconosciuti (italiano):
 *   "virgola" -> ,   "punto" -> .   "punto e virgola" -> ;   "due punti" -> :
 *   "punto interrogativo"/"punto di domanda" -> ?   "punto esclamativo" -> !
 *   "trattino" -> -   "apri parentesi" -> (   "chiudi parentesi" -> )
 *   "a capo"/"vai a capo"/"nuova riga" -> nuova riga
 *   "salva"/"salva nota"/"invia" -> esegue il salvataggio (solo se onVoiceSave è definito)
 *
 * Props principali:
 *  - value: string
 *  - onValueChange: (nuovoTesto: string) => void
 *  - onVoiceSave?: (testoCorrente: string) => void  // se presente, il comando "salva" salva la nota
 *  - lang: default "it-IT"
 *  - Tutti gli altri props (rows, placeholder, id, className, data-testid...) vengono inoltrati alla Textarea.
 */

const PUNCT_PHRASES = [
  ["punto e virgola", ";"],
  ["punto interrogativo", "?"],
  ["punto di domanda", "?"],
  ["punto esclamativo", "!"],
  ["due punti", ":"],
  ["apri parentesi", "("],
  ["chiudi parentesi", ")"],
  ["virgola", ","],
  ["trattino", "-"],
  ["punto", "."],
];

const NEWLINE_PHRASES = ["a capo", "vai a capo", "andare a capo", "nuova riga", "accapo"];
// "punto e a capo" -> chiude la frase con un punto e va a capo
const PERIOD_NEWLINE_PHRASES = ["punto e a capo", "punto a capo", "punto e vai a capo", "punto a nuova riga"];
// Ordinate dalla più lunga alla più corta per privilegiare la corrispondenza estesa
const SAVE_PHRASES = ["salva la nota", "salva nota", "invia la nota", "invia nota", "salva", "invia"];
// Modalità MAIUSCOLO: attiva/disattiva la scrittura in tutto maiuscolo
const CAPS_ON_PHRASES = ["tutto maiuscolo", "tutte maiuscole", "in maiuscolo"];
const CAPS_OFF_PHRASES = ["fine maiuscolo", "basta maiuscolo", "stop maiuscolo", "fine maiuscole", "minuscolo"];
// Comandi di modifica (operano sul testo già presente)
const DELETE_SENTENCE_PHRASES = ["cancella ultima frase", "cancella l'ultima frase", "cancella frase", "elimina ultima frase"];
const DELETE_WORD_PHRASES = ["cancella ultima parola", "cancella l'ultima parola", "cancella parola", "elimina ultima parola"];
const DELETE_SENTENCE_SET = new Set(DELETE_SENTENCE_PHRASES);
const DELETE_WORD_SET = new Set(DELETE_WORD_PHRASES);

const capitalizeWord = (w) => (w ? w.charAt(0).toLocaleUpperCase("it-IT") + w.slice(1) : w);

// Rimuove l'ultima frase dal testo (fino al precedente . ! ? o a capo).
export function deleteLastSentence(v) {
  const s = (v || "").replace(/[\s.!?]+$/, "");
  if (!s) return "";
  const idx = Math.max(s.lastIndexOf("."), s.lastIndexOf("!"), s.lastIndexOf("?"), s.lastIndexOf("\n"));
  if (idx === -1) return "";
  return s.slice(0, idx + 1).replace(/[ \t]+$/, "");
}

// Rimuove l'ultima parola dal testo.
export function deleteLastWord(v) {
  const s = (v || "").replace(/\s+$/, "");
  if (!s) return "";
  const idx = Math.max(s.lastIndexOf(" "), s.lastIndexOf("\n"));
  if (idx === -1) return "";
  return s.slice(0, idx).replace(/\s+$/, "");
}

// Trasforma un blocco di testo "finale" applicando i comandi vocali italiani.
// capStart indica se la prima parola va scritta in maiuscolo (inizio frase).
// capsMode indica se è attiva la modalità TUTTO MAIUSCOLO (persistente tra i blocchi).
// Ritorna { text, save, capsMode }.
export function formatDictation(chunk, { capStart = false, capsMode = false } = {}) {
  const words = (chunk || "").trim().split(/\s+/).filter(Boolean);
  const lower = words.map((w) => w.toLowerCase().replace(/[.,;:!?]+$/, ""));
  const N = words.length;
  let out = "";
  let save = false;
  let caps = capsMode;
  let capNext = capStart;
  let i = 0;

  const appendWord = (w) => {
    let word = w;
    if (caps) word = word.toLocaleUpperCase("it-IT");
    else if (capNext) word = capitalizeWord(word);
    capNext = false;
    if (!out || out.endsWith("\n") || out.endsWith(" ")) out += word;
    else out += " " + word;
  };
  const appendPunct = (p) => {
    out = out.replace(/[ \t]+$/, "");
    out += p;
    if (p === "." || p === "!" || p === "?") capNext = true;
  };
  const appendNewline = () => {
    out = out.replace(/[ \t]+$/, "");
    out += "\n";
    capNext = true;
  };

  const matchPhraseList = (list) => {
    for (const ph of list) {
      const parts = ph.split(" ");
      if (i + parts.length <= N) {
        let ok = true;
        for (let k = 0; k < parts.length; k++) {
          if (lower[i + k] !== parts[k]) { ok = false; break; }
        }
        if (ok) return parts.length;
      }
    }
    return 0;
  };

  while (i < N) {
    let m = matchPhraseList(SAVE_PHRASES);
    if (m) { save = true; i += m; continue; }

    m = matchPhraseList(CAPS_ON_PHRASES);
    if (m) { caps = true; i += m; continue; }

    m = matchPhraseList(CAPS_OFF_PHRASES);
    if (m) { caps = false; i += m; continue; }

    m = matchPhraseList(PERIOD_NEWLINE_PHRASES);
    if (m) { appendPunct("."); appendNewline(); i += m; continue; }

    m = matchPhraseList(NEWLINE_PHRASES);
    if (m) { appendNewline(); i += m; continue; }

    let punctHit = null;
    for (const [ph, ch] of PUNCT_PHRASES) {
      const parts = ph.split(" ");
      if (i + parts.length <= N) {
        let ok = true;
        for (let k = 0; k < parts.length; k++) {
          if (lower[i + k] !== parts[k]) { ok = false; break; }
        }
        if (ok) { punctHit = [parts.length, ch]; break; }
      }
    }
    if (punctHit) { appendPunct(punctHit[1]); i += punctHit[0]; continue; }

    appendWord(words[i]);
    i += 1;
  }
  return { text: out, save, capsMode: caps };
}

// Unisce il testo formattato al valore esistente rispettando spazi e punteggiatura.
export function joinAppended(existing, text) {
  if (!text) return existing || "";
  const cur = existing || "";
  if (!cur) return text;
  const startsPunct = /^[.,;:!?)]/.test(text);
  const curEndsBreak = /\s$/.test(cur) || cur.endsWith("\n");
  const sep = !startsPunct && !curEndsBreak ? " " : "";
  return cur + sep + text;
}

export const DictationTextarea = ({
  value = "",
  onValueChange,
  onVoiceSave,
  lang = "it-IT",
  className = "",
  ...props
}) => {
  const testId = props["data-testid"];
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");

  const recognitionRef = useRef(null);
  const listeningRef = useRef(false);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onValueChange);
  const onSaveRef = useRef(onVoiceSave);
  const stopRef = useRef(null);
  const capsModeRef = useRef(false);

  // Riferimenti sempre aggiornati (evita closure stantie nei callback del recognition)
  valueRef.current = value;
  onChangeRef.current = onValueChange;
  onSaveRef.current = onVoiceSave;

  const handleFinal = useCallback((chunk) => {
    // Comandi di modifica (operano sul testo esistente): riconosciuti se pronunciati da soli
    const norm = (chunk || "").trim().toLowerCase().replace(/[.,;:!?]+$/, "").replace(/\s+/g, " ");
    if (DELETE_SENTENCE_SET.has(norm)) { onChangeRef.current?.(deleteLastSentence(valueRef.current || "")); return; }
    if (DELETE_WORD_SET.has(norm)) { onChangeRef.current?.(deleteLastWord(valueRef.current || "")); return; }

    const cur = valueRef.current || "";
    const capStart = !cur || /[.!?]["')\]]?\s*$/.test(cur) || /\n\s*$/.test(cur);
    const { text, save, capsMode } = formatDictation(chunk, { capStart, capsMode: capsModeRef.current });
    capsModeRef.current = capsMode;
    const newVal = joinAppended(cur, text);
    if (newVal !== cur) onChangeRef.current?.(newVal);
    if (save && onSaveRef.current) {
      stopRef.current?.();
      onSaveRef.current(newVal);
    }
  }, []);

  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      setSupported(false);
      return;
    }
    setSupported(true);
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (event) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalChunk += res[0].transcript;
        else interimChunk += res[0].transcript;
      }
      if (finalChunk) handleFinal(finalChunk);
      setInterim(interimChunk);
    };

    rec.onerror = (e) => {
      const err = e?.error;
      if (err === "not-allowed" || err === "service-not-allowed") {
        listeningRef.current = false;
        setListening(false);
        setInterim("");
        toast.error("Permesso microfono negato. Abilita il microfono nelle impostazioni del browser.");
      } else if (err === "no-speech" || err === "aborted") {
        // gestiti da onend / stop manuale
      } else if (err) {
        toast.error(`Dettatura non disponibile (${err}).`);
      }
    };

    rec.onend = () => {
      if (listeningRef.current) {
        try {
          rec.start();
        } catch {
          listeningRef.current = false;
          setListening(false);
          setInterim("");
        }
      } else {
        setListening(false);
        setInterim("");
      }
    };

    recognitionRef.current = rec;

    return () => {
      listeningRef.current = false;
      try {
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.stop();
      } catch {
        /* no-op */
      }
      recognitionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, handleFinal]);

  const start = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    try {
      listeningRef.current = true;
      capsModeRef.current = false;
      setListening(true);
      setInterim("");
      rec.start();
    } catch {
      // start() invocato mentre già attivo: ignora
    }
  };

  const stop = useCallback(() => {
    listeningRef.current = false;
    capsModeRef.current = false;
    setListening(false);
    setInterim("");
    try {
      recognitionRef.current?.stop();
    } catch {
      /* no-op */
    }
  }, []);

  // Mantieni un riferimento aggiornato a stop() per l'uso nei callback del recognition
  stopRef.current = stop;

  const toggle = () => (listeningRef.current ? stop() : start());

  const hint = onVoiceSave
    ? "Comandi: «virgola», «a capo», «tutto maiuscolo», «cancella ultima frase», «salva»"
    : "Comandi: «virgola», «a capo», «tutto maiuscolo», «cancella ultima frase»";

  return (
    <div className="w-full">
      <div className="relative w-full">
        <Textarea
          {...props}
          value={value}
          onChange={(e) => onValueChange?.(e.target.value)}
          className={`pr-12 ${className}`}
        />
        {supported ? (
          <button
            type="button"
            onClick={toggle}
            aria-pressed={listening}
            aria-label={listening ? "Ferma dettatura vocale" : "Avvia dettatura vocale"}
            title={listening ? "Ferma dettatura" : "Detta con la voce (italiano)"}
            data-testid={testId ? `${testId}-mic` : "dictation-mic"}
            className={`absolute right-2 bottom-2 inline-flex h-8 w-8 items-center justify-center rounded-full border transition-[background-color,color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-blue-600)] ${
              listening
                ? "bg-[color:var(--brand-red-600)] text-white border-transparent animate-pulse"
                : "bg-white text-[color:var(--brand-blue-700)] border-slate-200 hover:bg-[color:var(--brand-blue-100)] hover:-translate-y-px"
            }`}>
            {listening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
        ) : (
          <span
            aria-hidden="true"
            title="Dettatura vocale non supportata su questo browser (usa Chrome o Edge)"
            data-testid={testId ? `${testId}-mic-unsupported` : "dictation-mic-unsupported"}
            className="absolute right-2 bottom-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-300">
            <MicOff className="h-4 w-4" />
          </span>
        )}
      </div>
      {listening && (
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-[color:var(--brand-red-600)]" data-testid={testId ? `${testId}-listening` : "dictation-listening"}>
          <span className="inline-block h-2 w-2 rounded-full bg-[color:var(--brand-red-600)] animate-pulse" />
          Sto ascoltando…
          <span className="text-muted-foreground">{hint}</span>
          {interim && <span className="text-muted-foreground italic truncate">· {interim}</span>}
        </p>
      )}
    </div>
  );
};

export default DictationTextarea;
